import React, { useState, useRef } from "react";
import { describeError } from "./errors";
import { api } from "./api";
import { IconFolder } from "./Icons";
import "./EnvironmentFilesPanel.css";

// Human-readable file size in KB/MB/GB, at most 2 decimals (trailing zeros
// trimmed). Anything under 0.01 KB shows as "<0.01 KB".
function formatFileSize(bytes) {
  const b = Number(bytes) || 0;
  const KB = 1024, MB = KB * 1024, GB = MB * 1024;
  const trim = (n) => String(Math.round(n * 100) / 100);
  if (b >= GB) return `${trim(b / GB)} GB`;
  if (b >= MB) return `${trim(b / MB)} MB`;
  const kb = b / KB;
  if (kb < 0.01) return "<0.01 KB";
  return `${trim(kb)} KB`;
}

// Show paths relative to the workspace root: strip the known sandbox / mount
// prefixes so e.g. "/mnt/session/outputs/data/x.csv" -> "data/x.csv" and a flat
// "notes.md" stays at the root. The original path is still used to open files.
function displayPath(p) {
  let s = (p || "").replace(/^\/+/, "");
  s = s.replace(/^mnt\/session\/outputs\//, "");
  s = s.replace(/^mnt\/session\/uploads\//, "");
  s = s.replace(/^workspace\/files\//, "");
  s = s.replace(/^mnt\/session\//, "");
  s = s.replace(/^workspace\//, "");
  return s;
}

// Build a nested folder tree from file paths (relative segments split on "/").
function buildFileTree(files) {
  const root = { dirs: {}, files: [] };
  for (const f of files) {
    const parts = displayPath(f.path).split("/").filter(Boolean);
    let node = root;
    for (let i = 0; i < parts.length - 1; i++) {
      const seg = parts[i];
      if (!node.dirs[seg]) node.dirs[seg] = { dirs: {}, files: [] };
      node = node.dirs[seg];
    }
    node.files.push(f);
  }
  return root;
}

function FileRow({ file, depth, selectedPath, onOpenFile, onContext }) {
  const basename = displayPath(file.path).split("/").pop() || file.path;
  return (
    <button
      className={`efp-row ${selectedPath === file.path ? "active" : ""}`}
      style={{ paddingLeft: 10 + depth * 14 }}
      onClick={() => onOpenFile?.(file.path)}
      onContextMenu={(e) => { e.preventDefault(); onContext?.(e, file); }}
      title={file.path}
    >
      <span className="efp-path">{basename}</span>
      {file.uploaded_by === "agent" && (
        <span className="efp-badge efp-badge--agent">agent</span>
      )}
      <span className="efp-size">{formatFileSize(file.size)}</span>
    </button>
  );
}

function FileFolder({ name, node, depth, selectedPath, onOpenFile, onContext }) {
  const [open, setOpen] = useState(true);
  return (
    <>
      <button
        className="efp-folder-row"
        style={{ paddingLeft: 10 + depth * 14 }}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className={`efp-folder-caret ${open ? "open" : ""}`} aria-hidden="true">›</span>
        <IconFolder size={13} />
        <span className="efp-folder-name">{name}</span>
      </button>
      {open && (
        <FileTreeLevel node={node} depth={depth + 1}
                       selectedPath={selectedPath} onOpenFile={onOpenFile}
                       onContext={onContext} />
      )}
    </>
  );
}

function FileTreeLevel({ node, depth, selectedPath, onOpenFile, onContext }) {
  const dirNames = Object.keys(node.dirs).sort((a, b) => a.localeCompare(b));
  const files = [...node.files].sort((a, b) => a.path.localeCompare(b.path));
  return (
    <>
      {dirNames.map((name) => (
        <FileFolder key={`dir:${name}`} name={name} node={node.dirs[name]}
                    depth={depth} selectedPath={selectedPath} onOpenFile={onOpenFile}
                    onContext={onContext} />
      ))}
      {files.map((f) => (
        <FileRow key={f.path} file={f} depth={depth}
                 selectedPath={selectedPath} onOpenFile={onOpenFile}
                 onContext={onContext} />
      ))}
    </>
  );
}

/**
 * Compact file list for the right-hand Files section: summary + upload/new +
 * the file list. Selecting a file calls onOpenFile(path); the full viewer /
 * editor opens in a modal (EnvironmentFileViewer) rather than inline, so it
 * has room to work in.
 */
export default function EnvironmentFilesPanel({
  files, loading, envId, envName, selectedPath,
  onOpenFile, onWrite, onDelete, onUploadDone,
}) {
  const [error, setError] = useState(null);
  const [uploads, setUploads] = useState({}); // { id: { name, progress, error } }
  const [isDragOver, setIsDragOver] = useState(false);
  const [ctxMenu, setCtxMenu] = useState(null); // { x, y, file }
  const fileInputRef = useRef(null);

  // Right-click menu on a file row. Position is clamped so the menu never
  // renders off-screen near the viewport edges.
  const openContextMenu = (e, file) => {
    const MENU_W = 160, MENU_H = 84;
    setCtxMenu({
      x: Math.min(e.clientX, window.innerWidth - MENU_W - 8),
      y: Math.min(e.clientY, window.innerHeight - MENU_H - 8),
      file,
    });
  };

  const ctxDownload = () => {
    const { file } = ctxMenu;
    setCtxMenu(null);
    const a = document.createElement("a");
    a.href = api.downloadEnvironmentFileUrl(envId, file.path);
    a.download = displayPath(file.path).split("/").pop() || file.path;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const ctxDelete = async () => {
    const { file } = ctxMenu;
    setCtxMenu(null);
    if (!window.confirm(`Delete "${displayPath(file.path)}"? This can't be undone.`)) return;
    try {
      await onDelete?.(file.path);
      // If the deleted file is open in the viewer, close it.
      if (selectedPath === file.path) onOpenFile?.(null);
      if (onUploadDone) onUploadDone();
    } catch (e2) {
      setError(describeError(e2) || "delete failed");
    }
  };

  const handleNew = async () => {
    const p = window.prompt("New file path (e.g., notes.md)");
    if (!p || !p.trim()) return;
    try {
      await onWrite(p.trim(), "");
      if (onUploadDone) onUploadDone();
      onOpenFile?.(p.trim());
    } catch (e) {
      setError(describeError(e) || "create failed");
    }
  };

  const handleFilesPicked = async (fileList) => {
    if (!envId) return;
    const picked = Array.from(fileList || []);
    for (const f of picked) {
      const id = `${f.name}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      setUploads((u) => ({ ...u, [id]: { name: f.name, progress: 0 } }));
      try {
        await api.uploadEnvironmentFile(
          envId, f, f.name,
          (p) => setUploads((u) => (
            u[id] ? { ...u, [id]: { ...u[id], progress: p } } : u
          )),
        );
        setUploads((u) => {
          const { [id]: _omit, ...rest } = u;
          return rest;
        });
        if (onUploadDone) onUploadDone();
      } catch (err) {
        setUploads((u) => (
          u[id]
            ? { ...u, [id]: { ...u[id], error: describeError(err) || String(err) } }
            : u
        ));
      }
    }
  };

  return (
    <div
      className={`efp-list-panel ${isDragOver ? "efp-list-panel--dragover" : ""}`}
      onDragOver={(e) => { if (!envId) return; e.preventDefault(); setIsDragOver(true); }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragOver(false);
        if (!envId) return;
        handleFilesPicked(e.dataTransfer.files);
      }}
    >
      <div className="efp-summary">
        <span>
          <strong>{files.length}</strong> file{files.length === 1 ? "" : "s"}
        </span>
        <div className="efp-summary-actions">
          <button
            className="ca-btn ca-btn--sm ca-btn--primary"
            onClick={() => fileInputRef.current?.click()}
            disabled={!envId}
          >
            Upload
          </button>
          <button className="ca-btn ca-btn--sm" onClick={handleNew} disabled={!envId}>+ New</button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: "none" }}
            onChange={(e) => { handleFilesPicked(e.target.files); e.target.value = ""; }}
          />
        </div>
      </div>

      {error && <div className="efp-error">{error}</div>}

      {Object.keys(uploads).length > 0 && (
        <div className="efp-uploads">
          {Object.entries(uploads).map(([id, u]) => (
            <div key={id} className={`efp-upload ${u.error ? "error" : ""}`}>
              <span className="efp-upload-name">{u.name}</span>
              {u.error
                ? <span className="efp-upload-err">{u.error}</span>
                : <progress max="1" value={u.progress} />}
            </div>
          ))}
        </div>
      )}

      <div className="efp-list">
        {loading && files.length === 0 && <div className="efp-empty">Loading…</div>}
        {!loading && files.length === 0 && (
          <div className="efp-empty">No files yet. Upload or drop files here.</div>
        )}
        {files.length > 0 && (
          <FileTreeLevel
            node={buildFileTree(files)}
            depth={0}
            selectedPath={selectedPath}
            onOpenFile={onOpenFile}
            onContext={openContextMenu}
          />
        )}
      </div>

      {ctxMenu && (
        <div
          className="efp-ctx-backdrop"
          onClick={() => setCtxMenu(null)}
          onContextMenu={(e) => { e.preventDefault(); setCtxMenu(null); }}
        >
          <div
            className="efp-ctx-menu"
            style={{ top: ctxMenu.y, left: ctxMenu.x }}
            onClick={(e) => e.stopPropagation()}
          >
            <button className="efp-ctx-item" onClick={ctxDownload}>Download</button>
            <button className="efp-ctx-item efp-ctx-item--danger" onClick={ctxDelete}>Delete…</button>
          </div>
        </div>
      )}
    </div>
  );
}
